import { useMutation, useQuery } from "@tanstack/react-query";
import { errorView } from "../helpers/ErrorHandler";
import { removeTokens } from "../utils/cacheStorage";
import { renameFile } from "../utils/imageUtils";
import { ApiServiceErr, axiosApi, customConfig } from "./apiService";

interface companyData {
  data: [
    {
      _id: string;
      createdAt: string;
      updatedAt: string;
      name: string;
      isParent: boolean;
      companyEmail: string;
      status: string;
      contacts: any;
    }
  ];
}

export const useGetCompanyDetails = () =>
  useQuery<companyData[], ApiServiceErr>(
    ["getCompanyData"],
    async () => {
      const response = await axiosApi.get("/company");
      return response?.data?.data || [];
    },
    {
      cacheTime: 0
    }
  );

export const useGetCompanyList = (pageNumber: number, pageLimit: number) =>
  useQuery(
    ["useGetCompanyList", pageNumber, pageLimit],
    async () => {
      const response = await axiosApi.get(
        `company/list?pageNumber=${pageNumber}&pageSize=${pageLimit}`
      );
      return response.data;
    },
    {
      cacheTime: 0
    }
  );

export const useGetCompanyById = (id?: any) =>
  useMutation<any, ApiServiceErr, any>(async id => {
    if (!id) {
      return null;
    }
    const response = await axiosApi.get(`company/${id}`);
    return response?.data?.data;
  }, id);

export const useUpdateCompanyData = (companyData?: any) =>
  useMutation<string, ApiServiceErr, any>(async companyData => {
    if (!companyData.id) {
      return null;
    }
    const response = await axiosApi.put(`company/${companyData.id}`, companyData.data);
    return response.data;
  }, companyData);

export const usePostRegisterCompanyData = (companyData?: any) =>
  useMutation<string, ApiServiceErr, any>(async companyData => {
    const response = await axiosApi.post(`company`, companyData);
    return response.data;
  }, companyData);

export const useUploadCompanyImage = () =>
  useMutation<any, ApiServiceErr, any>(
    async (data: { company_id: string; file: File; customFileName?: string }) => {
      // Rename file before upload

      // Use custom filename if provided, otherwise use company_id as filename
      const fileName = data.customFileName || data.company_id;
      const renamedFile = renameFile(data.file, fileName);

      const formData = new FormData();
      formData.append("companyId", data.company_id); // Backend expects 'companyId'
      formData.append("file", renamedFile);

      const response = await axiosApi.post(`/company/upload-logo`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      return response.data;
    }
  );

export const useGetCompanyLogoSignedUrl = (logoName?: string) =>
  useQuery<{ data: string; message: string; success: boolean }, ApiServiceErr>(
    ["companyLogoSignedUrl", logoName],
    async () => {
      const response = await axiosApi.get(`/company/logo/signed-url`, {
        params: { logoName }
      });
      return response.data;
    },
    {
      enabled: !!logoName,
      staleTime: 1000 * 60 * 4, // 4 minutes (fresh for 4 min before refetch)
      cacheTime: 1000 * 60 * 55, // 55 minutes (keep in cache)
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

export const useDeleteCompanyLogo = () =>
  useMutation<any, ApiServiceErr, string>(async (companyId: string) => {
    const response = await axiosApi.delete(`/company/logo/${companyId}`);
    return response.data;
  });

export const useDeleteCompanyData = (id?: any) =>
  useMutation<string, ApiServiceErr, any>(async id => {
    const response = await axiosApi.delete(`company/${id}`);
    return response.data;
  }, id);

export const useGetCompanySearch = (searchText?: any) =>
  useMutation<any, ApiServiceErr, any>(async searchText => {
    let newCustomConfig = { ...customConfig, params: { search: searchText } };
    const response = await axiosApi.get("/company", newCustomConfig);
    const company = response?.data?.data || [];

    company?.sort((a: any, b: any) => {
      const aIsNpAdmin = a.name === "NP Admin Company";
      const bIsNpAdmin = b.name === "NP Admin Company";

      if (aIsNpAdmin && !bIsNpAdmin) return -1;
      if (!aIsNpAdmin && bIsNpAdmin) return 1;

      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    const companyDetails = company?.map((company: any) => ({
      key: company._id,
      text: company.name,
      value: company._id,
      other: company
    }));

    return companyDetails || [];
  }, searchText);

export const useGetCompanyToken = (id?: any) =>
  useMutation<any, ApiServiceErr, any>(
    async id => {
      const response = await axiosApi.post(`/auth/change-tenant/${id}`);
      return response.data.data;
    },
    {
      onError: () => {
        removeTokens();
      }
    }
  );

export const useGetCompanyName = (token: any) =>
  useQuery<companyData[], ApiServiceErr>(
    ["getCompanyNames", token],
    async token => {
      const response = await axiosApi.get("/company", customConfig);
      const companyDetails = await response?.data?.data?.map((company: any) => {
        const newProduct: any = {
          id: company._id,
          title: company.name,
          tenantId: company.tenantId
        };
        return newProduct;
      });

      return companyDetails || [];
    },
    {
      cacheTime: 0,
      enabled: !!token,
      onError: () => {
        errorView("User hasn't any company");
        removeTokens();
      }
    }
  );
